/**
 * categoryRollup (#1617) — the category-grouped budget view's pure aggregation.
 *
 * Reads the SAME lifecycle records the chronological ledger reads, but folds them
 * by ``ledger_category`` instead of chronologically: planned records → Plan,
 * payed records → Ist (mirroring ``transactions.ts``'s record-role split). Two
 * windows per category — the SELECTED month and the Wirtschaftsjahr (Jul–Jun)
 * containing it — so the farmer reads "this month vs this WJ". Categories are
 * grouped under their L1 ``LedgerGroup`` (config order), only active categories
 * surface, and they sort by magnitude within a group.
 *
 * Pure + recomputed per render (no React/network), like ``summary.ts`` /
 * ``computeBalanceSeries`` — this is a READ-ONLY lens, it never touches the
 * projection / verdict / balance series.
 */
import {
  CATEGORY_META,
  L1_GROUPS,
  UNSORTED,
  type LedgerCategory,
  type LedgerDirection,
  type LedgerGroup,
} from './ledgerCategory';
import { groupRecords, signedTotal } from './transactions';
import type { LiquiditaetRecord, Transaction } from './types';

/** A 'YYYY-MM' window (inclusive both ends), compared lexicographically. */
export interface YmWindow {
  startYm: string;
  endYm: string;
}

/** Add ``months`` to a 'YYYY-MM' (wrapping the year), returning 'YYYY-MM'. */
function addMonthsToYm(ym: string, months: number): string {
  const [y, m] = ym.split('-').map(Number);
  const zero = y * 12 + (m - 1) + months;
  const ny = Math.floor(zero / 12);
  const nm = zero - ny * 12 + 1;
  return `${ny}-${String(nm).padStart(2, '0')}`;
}

/**
 * The Wirtschaftsjahr (Jul–Jun by default) window CONTAINING ``selectedYm``.
 * Mirrors ``monthLensItems``' WJ math: a month ≥ ``wjStartMonth`` belongs to the
 * WJ that starts that calendar year, else to the one that started the year before.
 */
export function wjWindowOf(selectedYm: string, wjStartMonth: number): YmWindow {
  const [y, m] = selectedYm.split('-').map(Number);
  const startYear = m >= wjStartMonth ? y : y - 1;
  const startYm = `${startYear}-${String(wjStartMonth).padStart(2, '0')}`;
  // 11 months after the start is the WJ's last month (Jul + 11 = next Jun).
  const endYm = addMonthsToYm(startYm, 11);
  return { startYm, endYm };
}

const inWindow = (ym: string, w: YmWindow): boolean => ym >= w.startYm && ym <= w.endYm;

/** A record's effective period ('YYYY-MM'): ``period_ym`` else the value-date month. */
const recordYm = (r: LiquiditaetRecord): string => r.period_ym || r.value_date.slice(0, 7);

/** Whether a record falls in the WJ window CONTAINING ``selectedYm``. Exported so the
 *  detail line can show only the in-window records that the bars actually counted. */
export function recordInWjWindow(
  r: LiquiditaetRecord,
  selectedYm: string,
  wjStartMonth: number,
): boolean {
  return inWindow(recordYm(r), wjWindowOf(selectedYm, wjStartMonth));
}

/** Per-category Plan/Ist for both columns + the contributing transactions. */
export interface CategoryRollup {
  id: LedgerCategory;
  label: string;
  group: LedgerGroup;
  direction: LedgerDirection;
  /** Σ planned ``total_eur`` for the selected month. */
  planMonth: number;
  /** Σ payed ``total_eur`` for the selected month. */
  istMonth: number;
  /** Σ planned ``total_eur`` for the Wirtschaftsjahr containing the month. */
  planYear: number;
  /** Σ payed ``total_eur`` for the Wirtschaftsjahr containing the month. */
  istYear: number;
  /** Whether ANY planned record landed in the selected month — the "Kein Plan"
   *  signal (#2270). RECORD-PRESENCE, not ``planMonth > 0``: a real net-zero plan
   *  (or a ``total_eur=0`` planned line) still counts as "has a plan". */
  hasPlanMonth: boolean;
  /** Whether any planned record landed in the WJ window (#2270). */
  hasPlanYear: boolean;
  /** The grouped transactions in this category's WJ window (newest first). */
  txns: Transaction[];
}

/** An L1 group with its active categories + a rolled-up Plan/Ist (single-direction). */
export interface GroupRollup {
  id: LedgerGroup;
  label: string;
  direction: LedgerDirection;
  /** Σ of the categories' ``planMonth`` (meaningful for a single-direction group). */
  planMonth: number;
  istMonth: number;
  planYear: number;
  istYear: number;
  /** OR of the categories' ``hasPlanMonth`` — the group has a plan for the month if
   *  ANY of its categories does (#2270), decided per column independently of the year. */
  hasPlanMonth: boolean;
  /** OR of the categories' ``hasPlanYear`` (#2270). */
  hasPlanYear: boolean;
  categories: CategoryRollup[];
}

const round2 = (n: number): number => Math.round(n * 100) / 100;

/** The larger of |plan| / |ist| over the WJ year — the category's sort weight. */
const magnitude = (c: CategoryRollup): number => Math.max(c.planYear, c.istYear);

/**
 * Roll the records up by ``ledger_category``, then by L1 group, for the selected
 * month + its Wirtschaftsjahr. Only categories with plan-or-Ist activity in the WJ
 * window survive; empty groups are dropped; categories sort by magnitude desc.
 */
export function rollupByCategory(
  records: LiquiditaetRecord[],
  selectedYm: string,
  wjStartMonth: number,
): GroupRollup[] {
  const wj = wjWindowOf(selectedYm, wjStartMonth);
  const ymOf = recordYm;

  // Group ALL records into transactions FIRST (NOT a pre-windowed subset), so plan/
  // Ist are attributed to the CANONICAL category — not each raw record's own. A
  // categorized ungeplant line writes a planned record under the chosen category
  // while its payed sibling keeps its original category (often ``unsorted``); both
  // share a ``transaction_id``, so the canonical category (``Transaction.ledger_category``,
  // from the planned/earliest record — mirrors the chronological ledger) is where BOTH
  // total. Windowing BEFORE grouping would drop a planned sibling that sits just
  // outside the WJ, stranding the in-WJ payment under ``unsorted`` → dropped as
  // crosscutting, zeroing the Ist (codex P1/P2). So window PER RECORD when summing.
  const txns = groupRecords(records);

  // Accumulate sums + the contributing transactions per CANONICAL category.
  type Acc = {
    planMonth: number;
    istMonth: number;
    planYear: number;
    istYear: number;
    hasPlanMonth: boolean;
    hasPlanYear: boolean;
    txns: Transaction[];
  };
  const byCat = new Map<LedgerCategory, Acc>();
  const accOf = (cat: LedgerCategory): Acc => {
    let a = byCat.get(cat);
    if (!a) {
      a = { planMonth: 0, istMonth: 0, planYear: 0, istYear: 0, hasPlanMonth: false, hasPlanYear: false, txns: [] };
      byCat.set(cat, a);
    }
    return a;
  };

  /** Fold a single in-WJ planned/payed record into the accumulator (contracted
   *  records carry no column — consumption bar = planned vs payed only). Returns
   *  whether the record contributed (so a transaction with no in-window plan/Ist
   *  is not listed as a phantom booking). */
  const addRecord = (a: Acc, r: LiquiditaetRecord | undefined): boolean => {
    if (!r || !inWindow(ymOf(r), wj)) return false;
    const inMonth = ymOf(r) === selectedYm;
    if (r.record_role === 'planned') {
      // Presence is amount-independent (#2270): a planned record — even ``total_eur=0``
      // — flips the flag, so a real net-zero plan is NEVER mistaken for "Kein Plan".
      a.hasPlanYear = true;
      a.planYear += r.total_eur;
      if (inMonth) {
        a.hasPlanMonth = true;
        a.planMonth += r.total_eur;
      }
      return true;
    }
    if (r.record_role === 'payed') {
      a.istYear += r.total_eur;
      if (inMonth) a.istMonth += r.total_eur;
      return true;
    }
    return false;
  };

  for (const tx of txns) {
    const a = accOf(tx.ledger_category); // canonical category for the whole transaction
    // Window each record individually (a transaction's plan + payment can fall in
    // different months/years), summing only the in-WJ plan/Ist amounts.
    const contributedPlan = addRecord(a, tx.planned);
    const contributedIst = addRecord(a, tx.payed);
    // List the transaction in its category's detail ONLY when it actually
    // contributes a plan or Ist in the window — a contracted-only (or fully
    // out-of-window) transaction would otherwise show as a phantom "Ist 0 · kein
    // Plan" booking (codex P2).
    if (contributedPlan || contributedIst) a.txns.push(tx);
  }

  // Bucket the active categories under their L1 group, preserving config order.
  // The budget lens covers ONLY the 6 functional L1 groups (#1617) — the
  // ``crosscutting`` bucket (vat/depreciation/unsorted) is deliberately omitted,
  // so it never becomes the ``groupById`` entry that would let those rows total.
  const groups: GroupRollup[] = L1_GROUPS.filter((g) => g.id !== 'crosscutting').map((g) => ({
    id: g.id,
    label: g.label,
    direction: g.direction,
    planMonth: 0,
    istMonth: 0,
    planYear: 0,
    istYear: 0,
    hasPlanMonth: false,
    hasPlanYear: false,
    categories: [] as CategoryRollup[],
  }));
  const groupById = new Map(groups.map((g) => [g.id, g]));

  for (const [cat, a] of byCat) {
    const meta = CATEGORY_META[cat];
    const group = groupById.get(meta.group);
    if (!group) continue; // crosscutting (vat/depreciation/unsorted) has no budget lens
    // Record-presence (#2270) is OR-ed into the group BEFORE the active-drop filter: a
    // ``total_eur=0`` plan-only category is dropped from RENDERING (no plan sum, no Ist)
    // yet a planned record DOES exist, so the group/hero must not fall back to "Kein Plan"
    // (codex r1). Crosscutting categories already ``continue``d above, so their plans
    // never leak into a budget group's presence.
    group.hasPlanMonth = group.hasPlanMonth || a.hasPlanMonth;
    group.hasPlanYear = group.hasPlanYear || a.hasPlanYear;
    // No plan AND no Ist in the WJ window → not an active (rendered) category.
    if (a.planYear === 0 && a.istYear === 0) continue;
    const cr: CategoryRollup = {
      id: cat,
      label: meta.label,
      group: meta.group,
      direction: meta.direction,
      planMonth: round2(a.planMonth),
      istMonth: round2(a.istMonth),
      planYear: round2(a.planYear),
      istYear: round2(a.istYear),
      hasPlanMonth: a.hasPlanMonth,
      hasPlanYear: a.hasPlanYear,
      txns: a.txns,
    };
    group.categories.push(cr);
    group.planMonth = round2(group.planMonth + cr.planMonth);
    group.istMonth = round2(group.istMonth + cr.istMonth);
    group.planYear = round2(group.planYear + cr.planYear);
    group.istYear = round2(group.istYear + cr.istYear);
  }

  for (const g of groups) {
    g.categories.sort((a, b) => magnitude(b) - magnitude(a));
  }

  // Drop empty groups (no active category).
  return groups.filter((g) => g.categories.length > 0);
}

/** Whether the period has ANY budget-relevant planned record, per column — the hero's
 *  "Kein Plan" signal (#2270). Deliberately INDEPENDENT of the category/group activity
 *  filters ``rollupByCategory`` applies for rendering: a plan that is dropped from the
 *  rendered tree (a ``total_eur=0`` plan-only category, OR a whole L1 group with no
 *  active category) still counts here, so the SALDO hero never falls back to "Kein Plan"
 *  while a plan exists in the period (codex r2). Crosscutting plans (vat/depreciation/
 *  unsorted) are excluded — they sit outside the budget lens, so they must not light up
 *  the hero. Presence is amount-independent, mirroring ``addRecord``. */
export function periodHasPlan(
  records: LiquiditaetRecord[],
  selectedYm: string,
  wjStartMonth: number,
): { month: boolean; year: boolean } {
  const wj = wjWindowOf(selectedYm, wjStartMonth);
  let month = false;
  let year = false;
  for (const tx of groupRecords(records)) {
    const r = tx.planned;
    if (!r) continue;
    // Attribute the plan to the transaction's CANONICAL category (mirrors the rollup),
    // and skip categories outside the 6 budget groups (crosscutting).
    const meta = CATEGORY_META[tx.ledger_category];
    if (!meta || meta.group === 'crosscutting') continue;
    const ym = recordYm(r);
    if (!inWindow(ym, wj)) continue;
    year = true;
    if (ym === selectedYm) month = true;
    if (year && month) break; // both windows satisfied — nothing more to learn
  }
  return { month, year };
}

/** The crosscutting categories that ARE real cash movements (#2347). ``unsorted``
 *  (the not-yet-categorised mass — 76 % of WF's records) + ``vat`` (USt/VSt cash)
 *  + ``transfer_internal`` (own-account movement, #2679).
 *  ``noncash_depreciation`` / ``noncash_inventory`` are accrual-only — they NEVER
 *  move the bank, so folding them into the hero would break the reconciliation
 *  invariant (hero saldo == Δ-balance). This is the PINNED treatment, not a free
 *  choice: it is what makes the category Saldo tie to the balance chart's total.
 *
 *  ``transfer_internal`` belongs here for exactly that reason: it is ``cash_only``,
 *  so each leg genuinely moves a bank balance. Over an all-accounts window holding
 *  BOTH legs it nets to 0 and changes nothing; but when only one leg is in scope —
 *  the single-account filter (#2664), or a transfer straddling a month boundary —
 *  the hero would otherwise drift from the balance chart by the full transfer
 *  amount, since ``rollupByCategory`` drops the whole crosscutting group. It is a
 *  CATEGORISED line, so (like ``vat``) it folds into the net but is deliberately
 *  NOT part of the still-uncategorised count/gross call-to-action below. */
const CROSSCUTTING_CASH: ReadonlySet<LedgerCategory> = new Set<LedgerCategory>([
  'unsorted',
  'vat',
  'transfer_internal',
]);

/**
 * The 'Unsortiert' pseudo-row (#2347): the net cash the budget lens omits.
 *
 * ``rollupByCategory`` drops the whole ``crosscutting`` bucket, so the uncategorised
 * (``unsorted``) mass — and the ``vat`` cash — never reaches the Kategorien hero saldo,
 * leaving it ~76 % short of the true period net on WF (it contradicts the balance chart).
 * This surfaces that omitted net so the hero can fold it in and reconcile.
 *
 * Windowed PER RECORD (via ``recordYm`` against the month + the ``wjWindowOf`` window,
 * exactly as ``rollupByCategory`` windows its Ist) — NOT ``unsortedShare``'s head-record
 * ``wjMonths`` month-list — so the folded net matches the rollup's Ist and the hero
 * reconciles. The net is a CASH figure: only ``payed`` records move the bank, so a
 * planned "unsorted" forecast neither nets nor counts.
 */
export interface UnsortedRollup {
  /** Σ signed ``payed`` ``total_eur`` (net Ist) of the crosscutting-cash transactions
   *  in the selected month … */
  istMonth: number;
  /** … and across the Wirtschaftsjahr window containing it. */
  istYear: number;
  /** The same net restricted to the still-``unsorted`` transactions — i.e. the Ist of
   *  exactly the population ``countMonth``/``grossMonth`` describe (#2679). The hero
   *  folds ``istMonth`` (all crosscutting cash, so the saldo ties to the Δ-balance);
   *  the 'Unsortiert' call-to-action ROW shows this, so its figure and its caption
   *  describe the same bookings. Before ``transfer_internal`` the two barely differed;
   *  one unpaired 400.000 € transfer leg makes them four orders of magnitude apart. */
  unsortedIstMonth: number;
  /** … and across the Wirtschaftsjahr window containing it. */
  unsortedIstYear: number;
  /** # of still-``unsorted`` transactions with a payment in the month / WJ — the
   *  actionable "categorise these" figure. ``vat`` is categorised, so it is NOT counted
   *  (it still folds into the net above, it just isn't part of the unsorted call-to-action). */
  countMonth: number;
  countYear: number;
  /** Σ |payed total| (sign-free gross) of those unsorted transactions — the secondary
   *  line, mirroring the header chip's gross figure. */
  grossMonth: number;
  grossYear: number;
  /** Whether ANY crosscutting-cash payment contributes in the month / WJ. Independent of
   *  the unsorted count (a vat-only period has activity but a zero unsorted count), so the
   *  hero can decide whether folding is relevant regardless of the row's visibility. */
  hasActivityMonth: boolean;
  hasActivityYear: boolean;
}

/**
 * Roll the crosscutting-CASH records (``unsorted`` + ``vat``) into the omitted-net
 * figures the 'Unsortiert' row + hero fold need, for the selected month and its
 * Wirtschaftsjahr. Pure, same windowing as ``rollupByCategory``.
 */
export function unsortedRollup(
  records: LiquiditaetRecord[],
  selectedYm: string,
  wjStartMonth: number,
): UnsortedRollup {
  const wj = wjWindowOf(selectedYm, wjStartMonth);
  let istMonth = 0;
  let istYear = 0;
  let unsortedIstMonth = 0;
  let unsortedIstYear = 0;
  let countMonth = 0;
  let countYear = 0;
  let grossMonth = 0;
  let grossYear = 0;
  let hasActivityMonth = false;
  let hasActivityYear = false;

  for (const tx of groupRecords(records)) {
    if (!CROSSCUTTING_CASH.has(tx.ledger_category)) continue;
    const p = tx.payed;
    if (!p) continue; // only a booking moves the bank — a planned forecast does not
    const ym = recordYm(p);
    if (!inWindow(ym, wj)) continue;
    const inMonth = ym === selectedYm;
    const signed = signedTotal(p);
    istYear += signed;
    hasActivityYear = true;
    if (inMonth) {
      istMonth += signed;
      hasActivityMonth = true;
    }
    // The count/gross secondary line — and the row's own Ist — are the
    // STILL-UNCATEGORISED call-to-action, so a categorised crosscutting line (`vat`,
    // `transfer_internal`) folds into the net above but contributes nothing here.
    if (tx.ledger_category === UNSORTED) {
      const gross = Math.abs(p.total_eur);
      unsortedIstYear += signed;
      countYear += 1;
      grossYear += gross;
      if (inMonth) {
        unsortedIstMonth += signed;
        countMonth += 1;
        grossMonth += gross;
      }
    }
  }

  return {
    istMonth: round2(istMonth),
    istYear: round2(istYear),
    unsortedIstMonth: round2(unsortedIstMonth),
    unsortedIstYear: round2(unsortedIstYear),
    countMonth,
    countYear,
    grossMonth: round2(grossMonth),
    grossYear: round2(grossYear),
    hasActivityMonth,
    hasActivityYear,
  };
}
